import { useState } from 'react';
import { Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { LogOut, UserCog, ChevronLeft, ChevronRight, Briefcase, FileText, Users, Car, BarChart3, MapPin, ClipboardCheck, IndianRupee, Wrench, FileSpreadsheet, BookOpen, Store, MessageCircle, PieChart } from 'lucide-react';
import Operations from './Operations';
import Accounts from './Accounts';
import RepairMaintenanceModule from './RepairMaintenanceModule';
import { cn } from '@/lib/utils';

const Dashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [activeSection, setActiveSection] = useState('operations');

  const handleSectionChange = (section) => {
    setActiveSection(section);
    navigate(`/dashboard/${section}`);
  };

  const menuItems = [
    {
      id: 'operations',
      label: 'Operations',
      icon: Briefcase,
      subItems: [
        { id: 'drivers', label: 'Drivers', icon: Users, path: '/dashboard/operations/drivers' },
        { id: 'vehicles', label: 'Vehicles', icon: Car, path: '/dashboard/operations/vehicles' },
        { id: 'fleet', label: 'Fleet Overview', icon: BarChart3, path: '/dashboard/operations/fleet' },
        { id: 'assets', label: 'Asset Tracking', icon: MapPin, path: '/dashboard/operations/assets' },
        { id: 'audits', label: 'Audits', icon: ClipboardCheck, path: '/dashboard/operations/audits' },
      ]
    },
    {
      id: 'accounts',
      label: 'Accounts',
      icon: FileText,
      subItems: [
        { id: 'hisaab', label: 'Weekly Hisaab', icon: FileSpreadsheet, path: '/dashboard/accounts/hisaab' },
        { id: 'driver-payments', label: 'Driver Payments', icon: IndianRupee, path: '/dashboard/accounts/driver-payments' },
        { id: 'outstandings', label: 'Outstandings', icon: BookOpen, path: '/dashboard/accounts/outstandings' },
        { id: 'vendor-ledger', label: 'Vendor Ledger', icon: Store, path: '/dashboard/accounts/vendor-ledger' },
        { id: 'whatsapp', label: 'WhatsApp Dispatch', icon: MessageCircle, path: '/dashboard/accounts/whatsapp' },
        { id: 'reports', label: 'Reports', icon: PieChart, path: '/dashboard/accounts/reports' },
        { id: 'repair-maintenance', label: 'Repair & Maintenance', icon: Wrench, path: '/dashboard/accounts/repair-maintenance' },
      ]
    },
    {
      id: 'rm',
      label: 'R&M',
      icon: Wrench,
      path: '/dashboard/rm',
    }
  ];

  return (
    <div className="flex h-screen bg-gray-50" data-testid="dashboard">
      {/* Sidebar */}
      <aside
        className={cn(
          'bg-white border-r border-gray-200 transition-all duration-300 flex flex-col',
          sidebarCollapsed ? 'w-16' : 'w-64'
        )}
      >
        {/* Logo */}
        <div className="h-20 border-b border-gray-200 flex items-center justify-between px-4">
          {!sidebarCollapsed && (
            <div className="flex items-center gap-2">
              <img 
                src="https://customer-assets.emergentagent.com/job_transport-ops-dash-1/artifacts/3bagp3xu_National%20Mobility.jpeg" 
                alt="National Mobility"
                className="h-16 w-auto"
              />
            </div>
          )}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
            className="ml-auto"
            data-testid="sidebar-toggle"
          >
            {sidebarCollapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
          </Button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto py-4">
          {menuItems.map((item) => (
            <div key={item.id} className="mb-2">
              <button
                onClick={() => {
                  if (item.subItems) {
                    setActiveSection(activeSection === item.id ? '' : item.id);
                  } else {
                    handleSectionChange(item.id);
                  }
                }}
                className={cn(
                  'w-full flex items-center gap-3 px-4 py-3 text-sm font-medium transition-colors',
                  location.pathname.includes(item.id)
                    ? 'bg-blue-50 text-blue-600 border-r-2 border-blue-600'
                    : 'text-gray-700 hover:bg-gray-50'
                )}
                data-testid={`menu-${item.id}`}
              >
                <item.icon className="w-5 h-5 flex-shrink-0" />
                {!sidebarCollapsed && <span>{item.label}</span>}
              </button>
              
              {/* Sub Items */}
              {item.subItems && activeSection === item.id && !sidebarCollapsed && (
                <div className="ml-4 mt-1 space-y-1">
                  {item.subItems.map((subItem) => (
                    <button
                      key={subItem.id}
                      onClick={() => navigate(subItem.path)}
                      className={cn(
                        'w-full flex items-center gap-3 px-4 py-2 text-sm transition-colors rounded-md',
                        location.pathname === subItem.path
                          ? 'bg-blue-50 text-blue-600 font-medium'
                          : 'text-gray-600 hover:bg-gray-50'
                      )}
                      data-testid={`submenu-${subItem.id}`}
                    >
                      <subItem.icon className="w-4 h-4" />
                      <span>{subItem.label}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          ))}
        </nav>

        {/* User Section */}
        <div className="border-t border-gray-200 p-4">
          {!sidebarCollapsed ? (
            <div className="space-y-2">
              <div className="text-sm">
                <p className="font-medium text-gray-900 truncate">{user?.email}</p>
                <p className="text-xs text-gray-500 capitalize">{user?.role}</p>
              </div>
              <div className="flex gap-2">
                {user?.role === 'admin' && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => navigate('/admin')}
                    className="flex-1"
                    data-testid="admin-panel-btn"
                  >
                    <UserCog className="w-4 h-4 mr-1" />
                    Admin
                  </Button>
                )}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={logout}
                  className="flex-1"
                  data-testid="logout-btn"
                >
                  <LogOut className="w-4 h-4 mr-1" />
                  Logout
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-2">
              {user?.role === 'admin' && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => navigate('/admin')}
                  className="w-full p-2"
                  data-testid="admin-panel-btn"
                >
                  <UserCog className="w-4 h-4" />
                </Button>
              )}
              <Button
                variant="outline"
                size="sm"
                onClick={logout}
                className="w-full p-2"
                data-testid="logout-btn"
              >
                <LogOut className="w-4 h-4" />
              </Button>
            </div>
          )}
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 overflow-y-auto">
        <div className="container mx-auto px-6 py-6">
          <Routes>
            <Route path="/" element={<Operations />} />
            <Route path="/operations/*" element={<Operations />} />
            <Route path="/accounts/*" element={<Accounts />} />
            <Route path="/rm" element={<RepairMaintenanceModule />} />
          </Routes>
        </div>
      </main>
    </div>
  );
};

export default Dashboard;
